import * as THREE from 'three';
import { POI } from '../../poi/POI';
import { POIData } from '../../poi/POIData';
import { POISystemState } from '../../poi/POISystemState';
import { Mesh3D } from '../../shapes/Mesh3D';
import { Logger } from '../../core/Logger';
import { MaterialLoadingManager } from '../../materials/MaterialLoadingManager';
import { Vector3, Quaternion } from '../../types';
import { InterpolatedProperty } from '../../properties/InterpolatedProperty';
import { Easing } from '../../types/easing';
import { TransformPropertyMode } from '../../core/Transform';
import { Transform } from '../../core/Transform';

export class DebugManager {
    constructor(
        private systemState: POISystemState,
        private sceneSettings: any,
        private appSettings: any,
        private materialLoadingManager: MaterialLoadingManager,
        private addPOI: (poi: POI) => void,
        private clearAllPOIs: () => void,
        private setActivePOI: (poi: POI) => void,
        private getPOIs: () => POI[],
        private getCamera: () => any,
        private getScene: () => any,
        private getGizmoCanvas: () => HTMLCanvasElement | null,
        private getGizmoRenderer: () => THREE.WebGLRenderer | null,
        private getGizmoScene: () => THREE.Scene | null,
        private getGizmoCamera: () => THREE.OrthographicCamera | null,
        private getGizmoAxisHelper: () => THREE.Group | null
    ) {}

    public debugMaterialLoading(): void {
        const debug = this.materialLoadingManager.getDebugInfo();
        
        //Logger.Instance.Info('🔍 Material Loading Debug Info:', 'MaterialLoading');
        //Logger.Instance.Info(`  Total: ${debug.totalItems}, Loaded: ${debug.loadedCount}, Failed: ${debug.failedCount}, Hanging: ${debug.hangingCount}`, 'MaterialLoading');
        
        if (debug.hangingItems.length > 0) {
            Logger.Instance.Warn('⚠️ Hanging materials:', 'MaterialLoading');
            debug.hangingItems.forEach((item: any) => {
                Logger.Instance.Warn(`  - ${item.type}: ${item.url} (${item.timeElapsed}s)`, 'MaterialLoading');
            });
        }
        
        if (debug.failedItems.length > 0) {
            Logger.Instance.Error('❌ Failed materials:', 'MaterialLoading');
            debug.failedItems.forEach((item: any) => {
                Logger.Instance.Error(`  - ${item.type}: ${item.url}`, 'MaterialLoading');
            });
        }
        
        if (debug.loadedItems.length > 0) {
            //Logger.Instance.Info('✅ Successfully loaded materials:', 'MaterialLoading');
            debug.loadedItems.forEach((item: any) => {
                //Logger.Instance.Debug(`  - ${item.type}: ${item.url}`, 'MaterialLoading');
            });
        }
    }

    public getCurrentTestObject(): Mesh3D | null {
        return this.systemState.activePOI?.shape ?? null;
    }

    public showTorus(): void {
        this.clearTestObject();
        const poidata = new POIData(this.sceneSettings, this.appSettings);
        poidata.shape.value = "torus";
        const poi = new POI("TestTorusPOI", poidata);
        this.addPOI(poi);
        const pois = this.getPOIs();
        if (pois.length > 0) {
            this.setActivePOI(pois[0]);
        }
    }

    public showTriangle(): void {
        this.clearTestObject();
        const poidata = new POIData(this.sceneSettings, this.appSettings);
        poidata.shape.value = "triangle";
        const poi = new POI("TestTrianglePOI", poidata);
        this.addPOI(poi);
        const pois = this.getPOIs();
        if (pois.length > 0) {
            this.setActivePOI(pois[0]);
        }
    }

    private clearTestObject(): void {
        this.clearAllPOIs();
    }

    public showSphere(): void {
        this.clearTestObject();
        const poidata = new POIData(this.sceneSettings, this.appSettings);
        poidata.shape.value = "sphere";
        const poi = new POI("TestSpherePOI", poidata);
        this.addPOI(poi);
        const pois = this.getPOIs();
        if (pois.length > 0) {
            this.setActivePOI(pois[0]);
        }
    }

    public showCube(): void {
        this.clearTestObject();
        const poidata = new POIData(this.sceneSettings, this.appSettings);
        poidata.shape.value = "box";
        const poi = new POI("TestCubePOI", poidata);
        this.addPOI(poi);
        const pois = this.getPOIs();
        if (pois.length > 0) {
            this.setActivePOI(pois[0]);
        }
    }

    public showPlane(): void {
        this.clearTestObject();
        const poidata = new POIData(this.sceneSettings, this.appSettings);
        poidata.shape.value = "plane";
        const poi = new POI("TestPlanePOI", poidata);
        this.addPOI(poi);
        const pois = this.getPOIs();
        if (pois.length > 0) {
            this.setActivePOI(pois[0]);
        }
    }

    public showHexagon(): void {
        this.clearTestObject();
        const poidata = new POIData(this.sceneSettings, this.appSettings);
        const poi = new POI("TestHexagonPOI", poidata);
        this.addPOI(poi);
        const pois = this.getPOIs();
        if (pois.length > 0) {
            this.setActivePOI(pois[0]);
        }
    }

    public runTransformTests(): void {
        //Logger.Instance.Info('--- Running Transform Tests ---');
    
        // --- Test Case 1: Simple Property with instance value ---
        const simple_t1 = new Transform(new Vector3(1, 2, 3), null, null, TransformPropertyMode.SIMPLE);
        //Logger.Instance.Info(`[Simple-1] Instance Value: Expected (1,2,3) -> Actual ${simple_t1.position.value?.toString()}`);
    
        // --- Test Case 2: Simple Property falling back to its own default value ---
        const simple_t2 = new Transform(null, null, null, TransformPropertyMode.SIMPLE, true);
        //Logger.Instance.Info(`[Simple-2] Own Default: Expected (0,0,0) -> Actual ${simple_t2.position.value?.toString()}`);
    
        // --- Test Case 3: Simple Property inheriting from a parent ---
        const simple_parent = new Transform(new Vector3(10, 20, 30), null, null, TransformPropertyMode.SIMPLE);
        const simple_child = Transform.createInheritable(TransformPropertyMode.SIMPLE);
        simple_child.position.addParent(simple_parent.position);
        //Logger.Instance.Info(`[Simple-3] Parent Inheritance: Expected (10,20,30) -> Actual ${simple_child.position.value?.toString()}`);
    
        // --- Test Case 4: Simple Property inheriting parent's default (using factory) ---
        const simple_parent_default_f = Transform.createInheritable(TransformPropertyMode.SIMPLE);
        const simple_child_no_default_f = new Transform(null, null, null, TransformPropertyMode.SIMPLE, false);
        simple_child_no_default_f.position.addParent(simple_parent_default_f.position);
        //Logger.Instance.Info(`[Simple-4a] Parent Default (Factory): Expected (0,0,0) -> Actual ${simple_child_no_default_f.position.value?.toString()}`);

        // --- Test Case 4b: Simple Property inheriting parent's default (using constructor) ---
        const simple_parent_default_c = new Transform(null, null, null, TransformPropertyMode.SIMPLE, true);
        const simple_child_no_default_c = new Transform(null, null, null, TransformPropertyMode.SIMPLE, false);
        simple_child_no_default_c.position.addParent(simple_parent_default_c.position);
        //Logger.Instance.Info(`[Simple-4b] Parent Default (Constructor): Expected (0,0,0) -> Actual ${simple_child_no_default_c.position.value?.toString()}`);
    
        //Logger.Instance.Info('--- Interpolated Property Static Tests ---');
    
        // --- Test Case 5: Interpolated Property with instance value ---
        const interp_t1 = new Transform(new Vector3(5, 6, 7), null, null, TransformPropertyMode.INTERPOLATED);
        //Logger.Instance.Info(`[Interp-1] Instance Value: Expected (5,6,7) -> Actual ${interp_t1.position.value?.toString()}`);
    
        // --- Test Case 6: Interpolated Property falling back to its own default value ---
        const interp_t2 = new Transform(null, null, null, TransformPropertyMode.INTERPOLATED, true);
        //Logger.Instance.Info(`[Interp-2] Own Default: Expected (0,0,0) -> Actual ${interp_t2.position.value?.toString()}`);
    
        // --- Test Case 7: Interpolated Property inheriting from a parent ---
        const interp_parent = new Transform(new Vector3(50, 60, 70), null, null, TransformPropertyMode.INTERPOLATED);
        const interp_child = Transform.createInheritable(TransformPropertyMode.INTERPOLATED);
        interp_child.position.addParent(interp_parent.position);
        //Logger.Instance.Info(`[Interp-3] Parent Inheritance: Expected (50,60,70) -> Actual ${interp_child.position.value?.toString()}`);
    
        // --- Test Case 8: Interpolated Property inheriting parent's default ---
        const interp_parent_default = Transform.createInheritable(TransformPropertyMode.INTERPOLATED);
        const interp_child_no_default = new Transform(null, null, null, TransformPropertyMode.INTERPOLATED, false);
        interp_child_no_default.position.addParent(interp_parent_default.position);
        //Logger.Instance.Info(`[Interp-4] Parent Default Inheritance: Expected (0,0,0) -> Actual ${interp_child_no_default.position.value?.toString()}`);
    
        //Logger.Instance.Info('--- Visual Animation Test ---');
        //Logger.Instance.Info('Sphere will animate to (-5, 2, 0) over 2 seconds.');

        const spherePOI = this.getPOIs().find(p => p.name === 'SpherePOI');
        if (spherePOI) {
            const posProp = spherePOI.transform.position as InterpolatedProperty<Vector3>;
            posProp.setDuration(2.0);
            posProp.setEasing(Easing.easeInOutCubic);
            
            // We change the DATA, and the TRANSFORM should react by interpolating.
            spherePOI.poidata.position.value = new Vector3(-5, 2, 0);
        } else {
            Logger.Instance.Warn('Could not find SpherePOI to run visual animation test.');
        }

        //Logger.Instance.Info('--- End of Transform Tests ---');
    }

    public debugGizmo(): void {
        console.log('🧭 GIZMO DEBUG STATE:');
        const gizmoCanvas = this.getGizmoCanvas();
        const gizmoRenderer = this.getGizmoRenderer();
        const gizmoScene = this.getGizmoScene();
        const gizmoCamera = this.getGizmoCamera();
        const gizmoAxisHelper = this.getGizmoAxisHelper();
        
        console.log('🧭 Canvas:', gizmoCanvas);
        console.log('🧭 Renderer:', gizmoRenderer);
        console.log('🧭 Scene:', gizmoScene);
        console.log('🧭 Camera:', gizmoCamera);
        console.log('🧭 AxisHelper:', gizmoAxisHelper);
        
        if (gizmoScene) {
            console.log('🧭 Scene Children:', gizmoScene.children);
        }
        
        if (gizmoAxisHelper) {
            console.log('🧭 AxisHelper Children:', gizmoAxisHelper.children);
        }
        
        // Try to find main camera
        let mainCamera: THREE.Camera | null = null;
        const scene = this.getScene();
        if (scene) {
            mainCamera = scene.getThreeScene().children.find((obj: any) => obj instanceof THREE.Camera) as THREE.Camera;
        }
        const camera = this.getCamera();
        if (!mainCamera && camera) {
            mainCamera = camera.internalCamera;
        }
        
        console.log('🧭 Main Camera:', mainCamera);
        if (mainCamera) {
            console.log('🧭 Main Camera Quaternion:', mainCamera.quaternion);
        }
        
        // Force render once
        if (gizmoRenderer && gizmoScene && gizmoCamera) {
            gizmoRenderer.render(gizmoScene, gizmoCamera);
            console.log('🧭 Forced render complete');
        }
    }

    public debugCameraTarget(): void {
        const camera = this.getCamera();
        if (!camera) {
            console.warn('🧭 Camera not available for debug');
            return;
        }

        const target = camera.getTarget();
        if (target) {
            console.log('🧭 Debug Camera Target:');
            console.log('🧭 Target ID:', target.id);
            console.log('🧭 Target Position:', target.transform.position.value?.toString());
            console.log('🧭 Target Rotation:', target.transform.rotation.value?.toString());
            console.log('🧭 Target Scale:', target.transform.scale.value?.toString());
            console.log('🧭 Target WorldPosition:', target.transform.worldPosition.value?.toString());
            console.log('🧭 Target WorldQuaternion:', target.transform.worldQuaternion.value?.toString());
            console.log('🧭 Target WorldScale:', target.transform.worldScale.value?.toString());
            
            // Force update world transform to see if it changes
            const worldRotationBefore = target.transform.worldQuaternion.value?.toString();
            target.transform.getWorldQuaternion(); // This should trigger updateWorldTransform
            const worldRotationAfter = target.transform.worldQuaternion.value?.toString();
            
            console.log('🧭 WorldQuaternion before forced update:', worldRotationBefore);
            console.log('🧭 WorldQuaternion after forced update:', worldRotationAfter);
            console.log('🧭 Transform isDirty:', target.transform.isDirty);
            console.log('🧭 Transform isRotationDirty:', target.transform.isRotationDirty);
            
            // Also check if poi.shape exists and has different rotation
            if (target.shape && target.shape.transform) {
                console.log('🧭 Shape Transform:');
                console.log('🧭 Shape Rotation:', target.shape.transform.rotation.value?.toString());
                console.log('🧭 Shape WorldQuaternion:', target.shape.transform.worldQuaternion.value?.toString());
                
                // Compare POI vs Shape rotation
                const poiRot = target.transform.rotation.value;
                const shapeRot = target.shape.transform.rotation.value;
                console.log('🧭 POI vs Shape Rotation comparison:');
                console.log('🧭   POI  rotation:', poiRot?.toString());
                console.log('🧭   Shape rotation:', shapeRot?.toString());
                console.log('🧭   Are they equal?', (poiRot && shapeRot) ? poiRot.equals(shapeRot) : false);
                
                // Check which transform camera will use
                console.log('🧭 Camera will use: Shape Transform (FIXED)');
            } else {
                console.log('🧭 No shape transform - camera will use POI transform');
            }
        } else {
            console.warn('🧭 Camera target is not set');
        }
    }
}

